import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of, defer } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Member, User } from 'src/models';
import { Organization } from '../models';
import { RbacService } from './rbac.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly JWT_TOKEN = 'JWT_TOKEN';

  public loggedUser: User | null = null;
  public currentMember: Member | null = null;
  public memberSub = new BehaviorSubject<Member | null>(null);

  endpoint = environment.endpoint();
  uri = environment.uri();

  _urlLogin = '/login';
  _urlLogout = '/logout';
  _urlUseInvite = '/organization/use-invite';
  _urlUser = '/user';
  _urlPassRecovery = '/password-recovery';
  _urlChangePassword = '/change-password';
  _urlGetMember = '/member';
  _urlUploadImage = '/user/:id/image';
  _urlUpdateSuperAdmin = '/master/members/super-admins';
  _urlUpdateOrganizationAdmin = '/master/organizations/:id';

  private _bootstrap$?: Observable<boolean>;

  constructor(
    private _http: HttpClient,
    private _route: Router,
    private rbac: RbacService,
  ) {}

  // =========================
  // Bootstrap (F5 / refresh)
  // =========================
  bootstrapSession(): Observable<boolean> {
    if (this._bootstrap$) return this._bootstrap$;

    this._bootstrap$ = defer(() => {
      const token = this.getJwtToken();
      if (!token) {
        // sem token
        this.loggedUser = null;
        this.currentMember = null;
        this.memberSub.next(null);
        this.rbac.invalidate();
        this.rbac.setFallbackRole(null);
        return of(false);
      }

      return this.currentUser().pipe(
        tap((u) => (this.loggedUser = u)),

        switchMap(() => this.setCurrentMember()),

        switchMap((member) => {
          const workspaceId = member.workspaceId;
          this.rbac.invalidate();
          return this.rbac.ensureLoaded(workspaceId).pipe(map(() => true));
        }),

        catchError((err) => {
          console.log('bootstrapSession error:', err);
          this.doLogoutUser(false);
          return of(false);
        }),
      );
    }).pipe(shareReplay(1));

    return this._bootstrap$;
  }

  // =========================
  // Auth
  // =========================
  login(user: any) {
    const url = this.endpoint + this._urlLogin;
    return this._http.post<any>(url, user).pipe(
      tap({
        next: (data) => this.applySession(data, { redirectTo: 'onboarding' }),
        error: (error) => console.log(error?.error ?? error),
      }),
    );
  }

  logout() {
    const reqHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.getJwtToken()}`,
    });

    const url = this.endpoint + this._urlLogout;

    if (this.isLoggedIn()) {
      this._http.post(url, { token: this.getJwtToken() }, { headers: reqHeaders }).subscribe({
        next: () => {
          this.doLogoutUser(true);
          location.reload();
        },
        error: (error) => alert(error?.error ?? error),
      });
    }
  }

  isLoggedIn() {
    return !!this.getJwtToken();
  }

  currentUser() {
    const reqHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.getJwtToken()}`,
    });

    const url = this.endpoint + this._urlUser;
    return this._http.get<User>(url, { headers: reqHeaders });
  }

  /**
   * Centraliza “setar sessão” (login, useInvite, switch-workspace).
   */
  applySession(
    data: any,
    opts: { redirectTo?: 'onboarding' | 'home' | 'none'; expectedWorkspaceId?: string } = {},
  ) {
    this.loggedUser = data?.user ?? null;
    this.storeToken(data?.token);

    this._bootstrap$ = undefined;

    this.setCurrentMember()
      .pipe(
        switchMap((member) => {
          const workspaceId = (member as any)?.workspaceId;
          this.rbac.invalidate();
          return this.rbac.ensureLoaded(opts.expectedWorkspaceId ?? workspaceId);
        }),
      )
      .subscribe({
        error: (err) => console.log('applySession chain error:', err),
      });

    if (opts.redirectTo === 'onboarding') this._route.navigate(['/onboarding/welcome']);
    else if (opts.redirectTo === 'home') this._route.navigate(['/']);
  }

  // =========================
  // Token storage
  // =========================
  storeToken(token: string) {
    if (!token) return;
    localStorage.setItem(this.JWT_TOKEN, token);
    sessionStorage.setItem(this.JWT_TOKEN, token);
  }

  getJwtToken() {
    return sessionStorage.getItem(this.JWT_TOKEN) ?? localStorage.getItem(this.JWT_TOKEN);
  }

  private doLogoutUser(navigate = true) {
    this.removeToken();
    this.loggedUser = null;
    this.currentMember = null;
    this.memberSub.next(null);
    this.rbac.invalidate();
    // `invalidate()` é só invalidação de cache e preserva o role (ele é setado
    // antes de `ensureLoaded` nos fluxos de sessão). No logout o role tem que
    // sair junto, senão vaza para a próxima sessão.
    this.rbac.setFallbackRole(null);
    this._bootstrap$ = undefined;

    if (navigate) this._route.navigate(['/login']);
  }

  private removeToken() {
    localStorage.removeItem(this.JWT_TOKEN);
    sessionStorage.removeItem(this.JWT_TOKEN);
  }

  // =========================
  // Invite / Password / Profile
  // =========================
  public useInvite(accessCode: string, password: string) {
    const url = this.endpoint + this._urlUseInvite;

    return this._http.post<any>(url, { accessCode, password }).pipe(
      tap({
        next: (res) => this.applySession(res, { redirectTo: 'home' }),
        error: (error) => console.log(error?.error ?? error),
      }),
    );
  }

  // Aceite explícito de convite para quem já tem conta (não cria senha).
  public acceptOwnerInvite(accessCode: string) {
    const url = this.endpoint + '/organization/accept-invite';
    return this._http.post<any>(url, { accessCode });
  }

  forgotPassword(email: string) {
    const url = this.endpoint + this._urlPassRecovery;
    return this._http.post(url, { email, originUrl: this.uri });
  }

  editUser(form: any) {
    const reqHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.getJwtToken()}`,
    });

    const url = this.endpoint + this._urlUser;
    return this._http.put(url, form, { headers: reqHeaders });
  }

  changePassword(pass: string, newPass: string) {
    const reqHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.getJwtToken()}`,
    });

    const url = this.endpoint + this._urlUser + this._urlChangePassword;
    return this._http.put(url, { password: pass, newPassword: newPass }, { headers: reqHeaders });
  }

  changeFgtPassword(token: string, password: string) {
    const url = this.endpoint + this._urlPassRecovery + this._urlChangePassword;
    return this._http.put(url, { token, password });
  }

  // =========================
  // Member
  // =========================
  setCurrentMember() {
    const reqHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.getJwtToken()}`,
    });

    const url = this.endpoint + this._urlGetMember;

    return this._http.get<Member>(url, { headers: reqHeaders }).pipe(
      tap((member: Member) => {
        this.currentMember = member;
        if (this.currentMember && this.loggedUser) {
          (this.currentMember).user = this.loggedUser as any;
        }
        this.memberSub.next(member);

        // Alimenta a contingência do RBAC: se /rbac/me estiver indisponível, as
        // permissões passam a ser derivadas deste role.
        this.rbac.setFallbackRole(member?.role ?? null);
      }),
    );
  }

  // =========================
  // Uploads / Master
  // =========================
  uploadImages(file: File, userId: string) {
    const reqHeaders = new HttpHeaders({
      Authorization: `Bearer ${this.getJwtToken()}`,
      FormData: 'true',
    });

    const formData = new FormData();
    formData.append('file', file, file.name);

    const url = this.endpoint + this._urlUploadImage.replace(':id', userId);
    return this._http.post(url, formData, { headers: reqHeaders });
  }

  updateSuperAdmins(membersSuper: any) {
    const url = this.endpoint + this._urlUpdateSuperAdmin;
    return this._http.put(url, membersSuper);
  }

  updateOrganizationAdmin(org: Organization) {
    const url = this.endpoint + this._urlUpdateOrganizationAdmin.replace(':id', org.id);
    return this._http.put(url, org);
  }
}
